import { useState } from 'react';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import { joinOverrides, splitOverrides } from './shared/CredentialSelector';

const useCredentialStep = ({
    selectedMarketplace,
    selectedHotel,
    selectedAccount,
    selectedPartner,
    solePartnerId,
    isSuperAdmin,
    isPartner,
    isChainAdmin,
}) => {
    const [credentials, setCredentials]                     = useState([]);
    const [selectedCredential, setSelectedCredential]       = useState(null);
    const [hotelIdOverride, setHotelIdOverride]             = useState('');
    const [marketplaceSettingsDefs, setMarketplaceSettingsDefs] = useState([]);
    const [overrideDefs, setOverrideDefs]                   = useState([]);
    const [credentialOverrides, setCredentialOverrides]     = useState({});
    const [inlineCredName, setInlineCredName]               = useState('');
    const [inlineCredFields, setInlineCredFields]           = useState({});
    const [savingCreds, setSavingCreds]                     = useState(false);
    const [credSaveError, setCredSaveError]                 = useState(null);
    const [testingCreds, setTestingCreds]                   = useState(false);
    const [credTestResult, setCredTestResult]               = useState(null);
    const [showAddCredForm, setShowAddCredForm]             = useState(false);
    const [addCredEntityType, setAddCredEntityType]         = useState(null);
    const [credShareHotel, setCredShareHotel]               = useState(false);
    const [credShareChain, setCredShareChain]               = useState(false);

    const credentialsForMarketplace = () =>
        credentials.filter(c => String(c.marketplace_id) === String(selectedMarketplace?.id));

    const credEntityTarget = () => {
        if (isPartner && (selectedPartner || solePartnerId)) return { type: 'partner', id: selectedPartner || solePartnerId, label: 'Partner' };
        if ((isChainAdmin || isSuperAdmin) && selectedAccount) return { type: 'account', id: selectedAccount, label: 'Chain' };
        return { type: 'hotel', id: selectedHotel?.hotelid, label: 'Hotel' };
    };

    const credEntityOptions = () => {
        const opts = [];
        const pid = selectedPartner || solePartnerId;
        if ((isSuperAdmin || isPartner) && pid) opts.push({ type: 'partner', id: pid, label: 'Partner' });
        if ((isSuperAdmin || isChainAdmin || isPartner) && selectedAccount) opts.push({ type: 'account', id: selectedAccount, label: 'Chain' });
        if (selectedHotel) opts.push({ type: 'hotel', id: selectedHotel.hotelid, label: 'Hotel' });
        return opts;
    };

    // Called when transitioning from PMS step to Credentials step.
    // Resets form state and loads marketplace setting definitions.
    const loadForMarketplace = async () => {
        setSelectedCredential(null);
        setInlineCredName('');
        setInlineCredFields({});
        setCredSaveError(null);
        setMarketplaceSettingsDefs([]);
        setShowAddCredForm(false);
        try {
            const res  = await marketplacesettingsService.getSchema({ MARKETPLACEID: selectedMarketplace.id });
            const all  = res.settings || [];
            const defs = all.filter(s => s.required?.toLowerCase() === 'y');
            const overrides = all.filter(s => s.must_be_overridden?.toLowerCase() === 'y');
            setMarketplaceSettingsDefs(defs);
            setOverrideDefs(overrides);
            const fields = {};
            defs.forEach(d => { fields[d.SETTINGNAME] = ''; });
            setInlineCredFields(fields);
            const overrideFields = {};
            overrides.forEach(d => { overrideFields[d.SETTINGNAME] = ''; });
            setCredentialOverrides(overrideFields);
        } catch (err) {
            console.error('Failed to load marketplace settings', err);
        }
        const opts = credEntityOptions();
        setAddCredEntityType(opts[0]?.type || 'hotel');
    };

    const handleSaveInlineCredentials = async () => {
        if (!inlineCredName.trim()) { setCredSaveError('Credential name is required'); return; }
        const opts   = credEntityOptions();
        const target = opts.find(o => o.type === addCredEntityType) || opts[0];
        if (!target) { setCredSaveError('No entity available to save credentials to'); return; }
        const isAboveHotel = target.type === 'account' || target.type === 'partner';
        const missing = marketplaceSettingsDefs.filter(d => {
            if (isAboveHotel && d.SETTINGNAME === 'HOTELID') return false;
            return !inlineCredFields[d.SETTINGNAME]?.trim();
        });
        if (missing.length > 0) { setCredSaveError('All required fields must be filled in'); return; }
        if (isAboveHotel && !hotelIdOverride.trim()) {
            setCredSaveError(`Hotel ID is required when saving credentials at ${target.label} level`);
            return;
        }
        setSavingCreds(true);
        setCredSaveError(null);
        try {
            const settings = marketplaceSettingsDefs.map(d => ({
                name:  d.SETTINGNAME,
                value: (isAboveHotel && d.SETTINGNAME === 'HOTELID') ? 'N/A' : (inlineCredFields[d.SETTINGNAME] || ''),
            }));
            await entityMktSettingsService.saveCredentials(target.type, target.id, selectedMarketplace.id, inlineCredName.trim(), settings);
            const reloadRes = await entityMktSettingsService.listCredentials(target.type, target.id);
            const newCreds  = (reloadRes.credentials || []).map(c => ({ ...c, _entityType: target.type }));
            setCredentials(prev => {
                const others = prev.filter(c => !(String(c.marketplace_id) === String(selectedMarketplace.id) && c._entityType === target.type));
                return [...others, ...newCreds.filter(c => String(c.marketplace_id) === String(selectedMarketplace.id))];
            });
            const saved = newCreds.find(c => String(c.marketplace_id) === String(selectedMarketplace.id));
            if (saved) {
                setSelectedCredential(saved);
                // Save credential shares for partner-level credentials inline
                if (target.type === 'partner') {
                    const sourceId = selectedPartner || solePartnerId;
                    if (sourceId) {
                        const shares = [];
                        if (credShareHotel && selectedHotel?.hotelid) shares.push({ targetEntityType: 'hotel',   targetEntityId: selectedHotel.hotelid });
                        if (credShareChain && selectedAccount)        shares.push({ targetEntityType: 'account', targetEntityId: selectedAccount });
                        if (shares.length > 0 && saved.credential_id) {
                            try { await entityMktSettingsService.saveShares(target.type, sourceId, selectedMarketplace.id, saved.credential_id, shares); } catch { /* non-fatal */ }
                        }
                    }
                }
            }
            setShowAddCredForm(false);
            setInlineCredName('');
            setCredTestResult(null);
            const fields = {};
            marketplaceSettingsDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
            setInlineCredFields(fields);
        } catch (err) {
            setCredSaveError(err.response?.data?.message || 'Failed to save credentials');
        } finally {
            setSavingCreds(false);
        }
    };

    const handleTestInlineCredentials = async () => {
        if (!selectedMarketplace?.id || marketplaceSettingsDefs.length === 0) return;
        const settings = marketplaceSettingsDefs.map(d => ({ name: d.SETTINGNAME, value: inlineCredFields[d.SETTINGNAME] || '' }));
        setTestingCreds(true);
        setCredTestResult(null);
        try {
            const res = await entityMktSettingsService.testCredentials(selectedMarketplace.id, settings);
            setCredTestResult(res.data || res);
        } catch (err) {
            setCredTestResult({ success: false, message: err.response?.data?.message || 'Test failed' });
        } finally {
            setTestingCreds(false);
        }
    };

    // Returns an error object if credential state is invalid, otherwise null
    const validate = () => {
        const creds = credentialsForMarketplace();
        if (creds.length > 0 && !selectedCredential) return { connector: 'Please select a credential' };
        const credType     = selectedCredential?._entityType || credEntityTarget().type;
        const credAboveHotel = selectedCredential && (credType === 'account' || credType === 'partner');
        if (credAboveHotel) {
            const hotelIdDef = overrideDefs.find(d => d.SETTINGNAME === 'HOTELID' || d.SETTINGNAME === 'HOTEL_ID');
            if (hotelIdDef && !hotelIdOverride.trim()) return { hotelIdOverride: 'Hotel ID is required for chain/partner-level credentials' };
            const missingOther = overrideDefs.filter(d => d.SETTINGNAME !== 'HOTELID' && d.SETTINGNAME !== 'HOTEL_ID' && !credentialOverrides[d.SETTINGNAME]?.trim());
            if (missingOther.length > 0) return { hotelIdOverride: `${missingOther[0].SETTINGNAME} is required` };
        }
        return null;
    };

    // Builds the credential-related fields for the create/update payload
    const buildCredentialPayload = () => {
        if (!selectedCredential) return {};
        const entityType = selectedCredential._entityType || addCredEntityType || 'hotel';
        // Prefer the credential's own owning entity id (authoritative, returned by the picker);
        // fall back to the wizard's selected hierarchy.
        const entityId   = selectedCredential.entity_id
                         ?? (entityType === 'hotel'   ? selectedHotel?.hotelid
                          : entityType === 'partner' ? (selectedPartner || solePartnerId)
                          : selectedAccount);
        const nonHotelIdOverrides = Object.fromEntries(
            Object.entries(credentialOverrides).filter(([k, v]) => k !== 'HOTELID' && k !== 'HOTEL_ID' && v?.trim())
        );
        return {
            credential_account_id: entityType === 'account'  ? entityId : null,
            credential_hotel_id:   entityType === 'hotel'    ? entityId : null,
            credential_partner_id: entityType === 'partner'  ? entityId : null,
            credential_id:         selectedCredential.credential_id || null,
            credential_overrides:  Object.keys(nonHotelIdOverrides).length ? nonHotelIdOverrides : null,
        };
    };

    // THE SHARED SELECTOR'S IN-PICKER OVERRIDES (CredentialSelector overridesInPicker): the values as the one object it
    // edits, and a pick that takes the credential and its per-hotel values together — so every wizard using this hook
    // asks for them in the picker, with Save, the same way.
    const overrideValues = joinOverrides(hotelIdOverride, credentialOverrides);
    const pickWithOverrides = (cred, values) => {
        setSelectedCredential({ ...cred, _entityType: cred._entityType || cred.entity_type });
        const split = splitOverrides(values || {});
        setHotelIdOverride(split.hotelidOverride || '');
        const fields = {};
        overrideDefs.forEach(d => {
            if (['HOTELID', 'HOTEL_ID'].includes(d.SETTINGNAME)) return;
            fields[d.SETTINGNAME] = (split.credentialOverrides || {})[d.SETTINGNAME] || '';
        });
        setCredentialOverrides(fields);
    };

    return {
        // State
        credentials, setCredentials,
        selectedCredential, setSelectedCredential,
        hotelIdOverride, setHotelIdOverride,
        marketplaceSettingsDefs,
        overrideDefs,
        credentialOverrides, setCredentialOverrides,
        inlineCredName, setInlineCredName,
        inlineCredFields, setInlineCredFields,
        savingCreds,
        credSaveError, setCredSaveError,
        testingCreds,
        credTestResult, setCredTestResult,
        showAddCredForm, setShowAddCredForm,
        addCredEntityType, setAddCredEntityType,
        credShareHotel, setCredShareHotel,
        credShareChain, setCredShareChain,
        // Functions
        credentialsForMarketplace,
        credEntityOptions,
        credEntityTarget,
        loadForMarketplace,
        handleSaveInlineCredentials,
        handleTestInlineCredentials,
        buildCredentialPayload,
        validate,
        overrideValues,
        pickWithOverrides,
    };
};

export default useCredentialStep;
